import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import MarketOverviewCard from './components/MarketOverviewCard';
import TransferTrendChart from './components/TransferTrendChart';
import MarketAlertCard from './components/MarketAlertCard';
import LeagueAnalysisCard from './components/LeagueAnalysisCard';
import PlayerMovementCard from './components/PlayerMovementCard';
import RegulationUpdateCard from './components/RegulationUpdateCard';
import EmergingTalentCard from './components/EmergingTalentCard';
import MarketReportCard from './components/MarketReportCard';
import FilterPanel from './components/FilterPanel';
import LiveTransferAlerts from '../unified-metrics-dashboard/components/LiveTransferAlerts';

const MarketIntelligence = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    region: 'all',
    position: 'all',
    league: 'all',
    timeframe: '30days',
    minValue: '',
    maxValue: '',
    ageRange: '',
    alertTypes: {
      transfers: true,
      regulations: true,
      trends: true,
      talents: true
    }
  });

  const marketOverviewData = [
  {
    icon: "TrendingUp",
    title: "Active Transfers",
    value: "247",
    change: "+18%",
    changeType: "positive",
    description: "vs. last month"
  },
  {
    icon: "DollarSign",
    title: "Total Market Value",
    value: "€428M",
    change: "+24%",
    changeType: "positive",
    description: "Combined player valuations"
  },
  {
    icon: "Users",
    title: "Emerging Talents",
    value: "89",
    change: "+12%",
    changeType: "positive",
    description: "Identified this quarter"
  },
  {
    icon: "Globe",
    title: "Active Markets",
    value: "34",
    change: "0%",
    changeType: "neutral",
    description: "Countries with activity"
  }];


  const transferTrendData = [
  { month: "Jan", transfers: 45, value: 62 },
  { month: "Feb", transfers: 52, value: 71 },
  { month: "Mar", transfers: 48, value: 65 },
  { month: "Apr", transfers: 61, value: 84 },
  { month: "May", transfers: 58, value: 78 },
  { month: "Jun", transfers: 73, value: 98 },
  { month: "Jul", transfers: 89, value: 124 },
  { month: "Aug", transfers: 95, value: 136 },
  { month: "Sep", transfers: 82, value: 115 },
  { month: "Oct", transfers: 76, value: 102 },
  { month: "Nov", transfers: 68, value: 91 },
  { month: "Dec", transfers: 71, value: 96 }];


  const marketValueData = [
  { month: "Jan", transfers: 32, value: 45 },
  { month: "Feb", transfers: 38, value: 52 },
  { month: "Mar", transfers: 35, value: 48 },
  { month: "Apr", transfers: 42, value: 61 },
  { month: "May", transfers: 48, value: 68 },
  { month: "Jun", transfers: 55, value: 78 }];


  const marketAlerts = [
  {
    id: 1,
    priority: "high",
    title: "Premier League Club Seeking Striker",
    description: "Top-tier English club actively searching for African striker aged 21-25 with proven goal-scoring record. Budget: €15-20M.",
    time: "2 hours ago",
    category: "Transfer Opportunity"
  },
  {
    id: 2,
    priority: "medium",
    title: "FIFA Updates Work Permit Regulations",
    description: "New guidelines for international transfers affecting African players moving to European leagues. Effective from March 2026.",
    time: "5 hours ago",
    category: "Regulatory Update"
  },
  {
    id: 3,
    priority: "high",
    title: "Bundesliga Interest in West African Talent",
    description: "German club representatives scheduled to visit West Africa for talent scouting. Focus on midfielders and defenders.",
    time: "1 day ago",
    category: "Market Trend"
  },
  {
    id: 4,
    priority: "low",
    title: "Rising Market Values in East Africa",
    description: "Player valuations in East African leagues showing 15% increase quarter-over-quarter driven by international exposure.",
    time: "2 days ago",
    category: "Market Analysis"
  }];


  const leagueAnalysis = [
  {
    id: 1,
    name: "Premier League",
    country: "England",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_193c682ef-1771574869268.png",
    imageAlt: "Modern football stadium with green pitch under bright floodlights during evening match with packed stands",
    activeTransfers: "42",
    totalValue: "€156M",
    avgAge: "23.4",
    growth: "+28%",
    insight: "Highest demand for African strikers and wingers"
  },
  {
    id: 2,
    name: "La Liga",
    country: "Spain",
    image: "https://images.unsplash.com/photo-1696137664071-4ddeee4833b6",
    imageAlt: "Spanish football stadium with distinctive architecture and orange sunset sky illuminating the playing field",
    activeTransfers: "38",
    totalValue: "€124M",
    avgAge: "24.1",
    growth: "+22%",
    insight: "Growing interest in technical midfielders"
  },
  {
    id: 3,
    name: "Bundesliga",
    country: "Germany",
    image: "https://images.unsplash.com/photo-1561135706-4f32b42875f4",
    imageAlt: "German football stadium with modern design featuring yellow and black team colors and large video screens",
    activeTransfers: "35",
    totalValue: "€98M",
    avgAge: "22.8",
    growth: "+19%",
    insight: "Focus on young defensive talents"
  },
  {
    id: 4,
    name: "Serie A",
    country: "Italy",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_16e42d471-1770824309654.png",
    imageAlt: "Italian football stadium with classic architecture and blue seats surrounding pristine green pitch at dusk",
    activeTransfers: "29",
    totalValue: "€87M",
    avgAge: "24.6",
    growth: "+15%",
    insight: "Preference for experienced defenders"
  }];


  const playerMovements = [
  {
    id: 1,
    playerName: "Kwame Mensah",
    playerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_168b561b1-1763295228872.png",
    playerImageAlt: "Professional headshot of young African male footballer with short black hair in red team jersey smiling confidently",
    position: "Striker",
    age: 23,
    fromClub: "Accra Hearts",
    toClub: "Brighton & Hove",
    value: "€12.5M",
    date: "Jan 15, 2026",
    status: "completed"
  },
  {
    id: 2,
    playerName: "Amara Diallo",
    playerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1cbf9269f-1763292997539.png",
    playerImageAlt: "Athletic African male footballer with determined expression wearing blue training kit with short cropped hair",
    position: "Midfielder",
    age: 21,
    fromClub: "TP Mazembe",
    toClub: "RB Leipzig",
    value: "€8.2M",
    date: "Jan 20, 2026",
    status: "pending"
  },
  {
    id: 3,
    playerName: "Chidi Okafor",
    playerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_168b561b1-1763295228872.png",
    playerImageAlt: "Young African male footballer with bright smile in green team jersey with athletic build and short hair",
    position: "Defender",
    age: 24,
    fromClub: "Enyimba FC",
    toClub: "Sevilla FC",
    value: "€6.8M",
    date: "Jan 18, 2026",
    status: "completed"
  },
  {
    id: 4,
    playerName: "Youssef El-Sayed",
    playerImage: "https://img.rocket.new/generatedImages/rocket_gen_img_1cbf9269f-1763292997539.png",
    playerImageAlt: "Professional African male footballer with focused expression wearing white team jersey with neat short hairstyle",
    position: "Winger",
    age: 22,
    fromClub: "Al Ahly",
    toClub: "AS Monaco",
    value: "€15.3M",
    date: "Jan 22, 2026",
    status: "pending"
  }];


  const regulationUpdates = [
  {
    id: 1,
    title: "FIFA Updates International Transfer Certificate Process",
    description: "New digital system streamlines ITC applications for African players moving to European clubs. Processing time reduced from 14 to 7 days with enhanced verification protocols.",
    date: "Jan 20, 2026",
    organization: "FIFA",
    impact: "high"
  },
  {
    id: 2,
    title: "CAF Introduces Enhanced Player Registration Standards",
    description: "Confederation of African Football implements stricter documentation requirements for player transfers to ensure compliance with international standards and protect player rights.",
    date: "Jan 18, 2026",
    organization: "CAF",
    impact: "medium"
  },
  {
    id: 3,
    title: "UK Work Permit Points System Updated",
    description: "British FA adjusts points-based system for international player work permits. Changes affect criteria for African players seeking Premier League opportunities.",
    date: "Jan 15, 2026",
    organization: "FA",
    impact: "high"
  }];


  const emergingTalents = [
  {
    id: 1,
    name: "Ibrahim Kamara",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_18297e2de-1767565210597.png",
    imageAlt: "Young African male footballer in action on green pitch wearing red jersey dribbling ball with intense focus and athletic stance",
    position: "Forward",
    age: 19,
    currentClub: "ASEC Mimosas",
    country: "Ivory Coast",
    goals: 24,
    assists: 12,
    rating: "8.9",
    marketValue: "€4.2M"
  },
  {
    id: 2,
    name: "Aisha Mwangi",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1429efdf4-1768811768787.png",
    imageAlt: "Athletic African female footballer in blue team kit running with ball showing excellent technique and determination on grass field",
    position: "Midfielder",
    age: 20,
    currentClub: "Gor Mahia",
    country: "Kenya",
    goals: 15,
    assists: 18,
    rating: "8.7",
    marketValue: "€3.8M"
  },
  {
    id: 3,
    name: "Tunde Adeyemi",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1dc274a9e-1763295868785.png",
    imageAlt: "Young African male footballer in yellow jersey celebrating goal with arms raised showing joy and passion on football pitch",
    position: "Winger",
    age: 21,
    currentClub: "Kano Pillars",
    country: "Nigeria",
    goals: 19,
    assists: 14,
    rating: "8.8",
    marketValue: "€5.1M"
  }];


  const marketReports = [
  {
    id: 1,
    title: "Q1 2026 African Football Transfer Market Analysis",
    description: "Comprehensive quarterly report covering transfer trends, market valuations, and emerging opportunities across African football markets.",
    date: "Jan 25, 2026",
    pages: 48,
    type: "premium"
  },
  {
    id: 2,
    title: "West African Talent Pipeline Report",
    description: "In-depth analysis of emerging talents from West African leagues with performance metrics and European club interest indicators.",
    date: "Jan 20, 2026",
    pages: 32,
    type: "free"
  },
  {
    id: 3,
    title: "European League Demand Analysis 2026",
    description: "Detailed breakdown of position-specific demand across top European leagues with focus on African player opportunities.",
    date: "Jan 15, 2026",
    pages: 56,
    type: "premium"
  },
  {
    id: 4,
    title: "FIFA Regulatory Compliance Guide",
    description: "Complete guide to FIFA transfer regulations affecting African players with step-by-step compliance procedures.",
    date: "Jan 10, 2026",
    pages: 24,
    type: "free"
  }];


  const tabs = [
  { id: 'overview', label: 'Market Overview', icon: 'BarChart3' },
  { id: 'transfers', label: 'Transfer Activity', icon: 'ArrowRightLeft' },
  { id: 'alerts', label: 'Live Alerts', icon: 'Bell' },
  { id: 'reports', label: 'Reports', icon: 'FileText' }];


  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value
    }));
  };

  const handleResetFilters = () => {
    setFilters({
      region: 'all',
      position: 'all',
      league: 'all',
      timeframe: '30days',
      minValue: '',
      maxValue: '',
      ageRange: '',
      alertTypes: {
        transfers: true,
        regulations: true,
        trends: true,
        talents: true
      }
    });
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="relative bg-gradient-to-br from-primary via-primary to-secondary py-12 md:py-16 lg:py-20 overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-64 h-64 bg-white rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center justify-between gap-6 lg:gap-8">
              <div className="flex-1 text-center lg:text-left">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 rounded-full mb-4">
                  <Icon name="TrendingUp" size={16} color="white" />
                  <span className="text-xs md:text-sm font-medium text-white">Real-Time Market Intelligence</span>
                </div>
                <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
                  Market Intelligence Hub
                </h1>
                <p className="text-base md:text-lg text-white/90 max-w-2xl mx-auto lg:mx-0">
                  Data-driven insights, transfer trends, and real-time opportunities connecting African football excellence with European markets
                </p>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <Button variant="default" size="lg" iconName="Download" iconPosition="left" className="bg-white text-primary hover:bg-white/90">
                  Download Report
                </Button>
                <Button variant="outline" size="lg" iconName="Bell" iconPosition="left" className="border-white text-white hover:bg-white/10">
                  Set Alerts
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-12 lg:py-16">
          <div className="flex flex-col lg:flex-row gap-6 lg:gap-8 mb-8">
            <div className="flex-1">
              <div className="bg-card rounded-lg border border-border overflow-x-auto">
                <div className="flex items-center gap-1 p-2 min-w-max">
                  {tabs?.map((tab) =>
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors duration-300 whitespace-nowrap ${
                    activeTab === tab?.id ?
                    'bg-primary text-primary-foreground' :
                    'text-muted-foreground hover:text-foreground hover:bg-muted'}`
                    }>

                      <Icon name={tab?.icon} size={16} />
                      {tab?.label}
                    </button>
                  )}
                </div>
              </div>
            </div>
            <Button
              variant="outline"
              size="default"
              iconName="Filter"
              iconPosition="left"
              onClick={() => setShowFilters(!showFilters)}
              className="lg:w-auto">

              {showFilters ? 'Hide Filters' : 'Show Filters'}
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
            {showFilters &&
            <div className="lg:col-span-1">
                <FilterPanel
                filters={filters}
                onFilterChange={handleFilterChange}
                onReset={handleResetFilters} />

              </div>
            }

            <div className={showFilters ? 'lg:col-span-3' : 'lg:col-span-4'}>
              {activeTab === 'overview' &&
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6 mb-8">
                  {marketOverviewData?.map((item, index) =>
                <MarketOverviewCard
                  key={index}
                  icon={item?.icon}
                  title={item?.title}
                  value={item?.value}
                  change={item?.change}
                  changeType={item?.changeType}
                  description={item?.description} />

                )}
                </div>
              }

              {activeTab === 'overview' &&
              <div className="mb-8">
                  <LiveTransferAlerts />
                </div>
              }

              {activeTab === 'overview' &&
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
                  <TransferTrendChart
                  data={transferTrendData}
                  type="line"
                  title="Transfer Activity Trends"
                  description="Monthly transfer volume and market value over the past year" />

                  <TransferTrendChart
                  data={marketValueData}
                  type="bar"
                  title="Market Value Growth"
                  description="Quarterly market value progression for African players" />

                </div>
              }

              {activeTab === 'overview' &&
              <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-foreground">League Analysis</h2>
                    <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
                      View All
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                    {leagueAnalysis?.map((league) =>
                  <LeagueAnalysisCard key={league?.id} league={league} />
                  )}
                  </div>
                </div>
              }

              {activeTab === 'overview' &&
              <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl md:text-2xl font-bold text-foreground">Emerging Talents</h2>
                    <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
                      View All
                    </Button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                    {emergingTalents?.map((talent) =>
                  <EmergingTalentCard key={talent?.id} talent={talent} />
                  )}
                  </div>
                </div>
              }

              {activeTab === 'transfers' &&
              <div className="space-y-8">
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl md:text-2xl font-bold text-foreground">Recent Player Movements</h2>
                      <Select
                      options={[
                      { value: 'all', label: 'All Status' },
                      { value: 'completed', label: 'Completed' },
                      { value: 'pending', label: 'Pending' }]
                      }
                      value="all"
                      onChange={() => {}}
                      className="w-48" />

                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                      {playerMovements?.map((movement) =>
                    <PlayerMovementCard key={movement?.id} movement={movement} />
                    )}
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <h2 className="text-xl md:text-2xl font-bold text-foreground">Regulatory Updates</h2>
                      <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right">
                        View All
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                      {regulationUpdates?.map((update) =>
                    <RegulationUpdateCard key={update?.id} update={update} />
                    )}
                    </div>
                  </div>
                </div>
              }

              {activeTab === 'alerts' &&
              <div className="space-y-6">
                  <div className="bg-card rounded-lg border border-border p-4 md:p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                        <Icon name="Bell" size={20} color="var(--color-accent)" />
                      </div>
                      <div>
                        <h3 className="text-base md:text-lg font-bold text-foreground">Live Market Alerts</h3>
                        <p className="text-xs md:text-sm text-muted-foreground">Real-time notifications for transfer opportunities and market changes</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button variant="default" size="sm" iconName="Plus" iconPosition="left">
                        Create Alert
                      </Button>
                      <Button variant="outline" size="sm" iconName="Settings" iconPosition="left">
                        Manage Alerts
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {marketAlerts?.map((alert) =>
                  <MarketAlertCard key={alert?.id} alert={alert} />
                  )}
                  </div>
                </div>
              }

              {activeTab === 'reports' &&
              <div className="space-y-6">
                  <div className="bg-gradient-to-r from-primary to-secondary rounded-lg p-6 md:p-8 text-white">
                    <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                      <div>
                        <h3 className="text-xl md:text-2xl font-bold mb-2">Premium Intelligence Reports</h3>
                        <p className="text-sm md:text-base text-white/90">
                          Access exclusive market analysis and in-depth transfer insights
                        </p>
                      </div>
                      <Button variant="default" size="lg" iconName="Crown" iconPosition="left" className="bg-white text-primary hover:bg-white/90">
                        Upgrade Now
                      </Button>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                    {marketReports?.map((report) =>
                  <MarketReportCard key={report?.id} report={report} />
                  )}
                  </div>
                </div>
              }
            </div>
          </div>
        </div>

        <div className="bg-muted py-12 md:py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="text-center mb-8 md:mb-12">
              <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Stay Ahead of the Market
              </h2>
              <p className="text-base md:text-lg text-muted-foreground max-w-2xl mx-auto">
                Subscribe to receive weekly market insights, transfer alerts, and exclusive intelligence reports
              </p>
            </div>
            <div className="max-w-xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-3 rounded-lg border border-border bg-card text-foreground focus:outline-none focus:ring-2 focus:ring-primary" />

                <Button variant="default" size="lg" iconName="Mail" iconPosition="left" className="sm:w-auto">
                  Subscribe
                </Button>
              </div>
              <p className="text-xs text-muted-foreground text-center mt-3">
                Join 5,000+ industry professionals receiving our market intelligence updates
              </p>
            </div>
          </div>
        </div>

        <footer className="bg-card border-t border-border py-8 md:py-12">
          <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name="Trophy" size={24} color="var(--color-primary)" />
                </div>
                <span className="text-lg font-bold text-primary">ProX11Market</span>
              </div>
              <p className="text-sm text-muted-foreground text-center md:text-left">
                © {new Date()?.getFullYear()} ProX11Market. All rights reserved.
              </p>
              <div className="flex items-center gap-4">
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors duration-300">
                  <Icon name="Twitter" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors duration-300">
                  <Icon name="Linkedin" size={20} />
                </a>
                <a href="#" className="text-muted-foreground hover:text-primary transition-colors duration-300">
                  <Icon name="Instagram" size={20} />
                </a>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>);


};

export default MarketIntelligence;